/**
 * @typedef {{ essential: boolean, analytics: boolean, advertising: boolean }} CookieConsentFlags
 */

export const COOKIE_CONSENT_STORAGE_KEY = 'volugraph_cookie_consent';
export const COOKIE_CONSENT_STORAGE_VERSION = 2;

/**
 * @param {string} raw
 * @returns {{ acknowledged: boolean, at: string | null, analytics: boolean, advertising: boolean } | null}
 */
export function parseCookieConsentStorage(raw) {
  try {
    const p = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return null;

    if (p.version === COOKIE_CONSENT_STORAGE_VERSION && typeof p.acknowledgedAt === 'string') {
      return {
        acknowledged: true,
        at: p.acknowledgedAt,
        analytics: Boolean(p.analytics),
        advertising: Boolean(p.advertising),
      };
    }

    // Legacy v1 or pre-version payloads — essential only, no optional tracking.
    if (
      p.version === 1 ||
      typeof p.consentedAt === 'string' ||
      typeof p.analytics === 'boolean' ||
      typeof p.marketing === 'boolean'
    ) {
      return {
        acknowledged: true,
        at: typeof p.acknowledgedAt === 'string'
          ? p.acknowledgedAt
          : typeof p.consentedAt === 'string'
            ? p.consentedAt
            : null,
        analytics: false,
        advertising: false,
      };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * @param {{ analytics?: boolean, advertising?: boolean }} flags
 */
export function buildCookieConsentPayload(flags) {
  const analytics = Boolean(flags.analytics);
  const advertising = Boolean(flags.advertising);
  return {
    version: COOKIE_CONSENT_STORAGE_VERSION,
    essentialOnly: !analytics && !advertising,
    analytics,
    advertising,
    acknowledgedAt: new Date().toISOString(),
  };
}

/**
 * @returns {{ acknowledged: boolean, at: string | null, analytics: boolean, advertising: boolean } | null}
 */
export function loadCookieConsentFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return parseCookieConsentStorage(raw);
  } catch {
    return null;
  }
}

/**
 * @param {{ analytics?: boolean, advertising?: boolean }} flags
 */
export function persistCookieConsent(flags) {
  const payload = buildCookieConsentPayload(flags);
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
  return payload;
}

/**
 * @param {{ acknowledged?: boolean, at?: string | null, analytics?: boolean, advertising?: boolean } | null | undefined} stored
 * @returns {CookieConsentFlags}
 */
export function cookieConsentFlagsFromStored(stored) {
  return {
    essential: true,
    analytics: Boolean(stored?.analytics),
    advertising: Boolean(stored?.advertising),
  };
}
